import { preserveOrdinalStreamState } from 'state/ordinalStatePatch'
import cn from 'classnames'
import api, { IOrdinal, IOrdinalsMediaStreamItem } from 'api/api'
import { refetchStreamAfterExplicitInvalidation } from 'api/queryPolicies'
import { USD_UNIT } from 'lib/bsv-util'
import useStore from 'state/knovStore'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import { withDepth, Filter } from 'components/filters/useStreamFilters'
import {
    Box,
    Circle,
    FileText,
    Film,
    Globe,
    Image as ImageIcon,
    Maximize2,
    Music,
    X,
} from 'lucide-react'
import OrdinalsRender from 'components/answers/FileEmbed/OrdinalsRender'
import OrdinalActionDock from 'components/panels/OrdinalActionDock'
import ErrorBoundary from 'components/shared/ErrorBoundary'
import useStreamFilter from 'components/filters/useStreamFilters'
import { applyOrdinalsMinSats } from 'components/panels/OrdinalsMinSatsPill'
import { shouldRetryWalletSnapshotQuery } from 'components/panels/ordinalStreamUpdatePolicy'
import {
    formatOrdinalsMinSatsAmount,
    ordinalsStreamQueryFilter,
    resolveOrdinalsMinSats,
} from 'components/panels/ordinalsThreshold'
import { recordEngagementOpen, useQuestImpression } from 'lib/questImpressions'
import { isMobile } from 'lib/device'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { useInView } from 'react-intersection-observer'
import { resolveNextOrdinalMediaPage } from 'lib/ordinalMediaPagination'
import TreechatIcon from 'components/shared/icons/TreechatIcon'
import UserIcon from 'components/users/UserIcon'
import { useUserHoverCard } from 'components/users/UserHoverCard'
import OrdinalDetails from 'components/modals/OrdinalDetails'
import SendOrdinal from 'components/modals/SendOrdinal'
import viewerStyles from 'components/modals/ordinals-viewer.module.scss'
import styles from 'components/panels/ordinals-media-stream.module.scss'
import { getOrdinalControlsMode } from 'components/panels/ordinalUpvalueFeature'
import { ordinalsTopWindowParam } from 'components/panels/streamOrderMapping'
import { shouldShowOrdinalOwnerAvatar } from 'components/panels/ordinalOwnerAvatar'
import type { CombinedOrdinal, UsersOrdinal } from 'types/ordinals'

type OrdinalsMediaStreamProps = {
    filter: Filter
}

function getImageAlt(item: IOrdinalsMediaStreamItem) {
    const name =
        item?.parent?.user?.name || item?.parent?.user?.handle || item?.parent?.user?.username
    return name ? `Ordinal by ${name}` : 'Ordinal'
}

function normalizeContentType(ct?: string | null) {
    return (ct || '').toLowerCase()
}

function shouldAutoCapture3dPreview(item: IOrdinalsMediaStreamItem, inView: boolean) {
    if (!inView) return false
    const t = normalizeContentType(item.content_type)
    const isGltf =
        t.includes('model/gltf-binary') || t.includes('model/gltf+json') || t.includes('model/gltf')
    if (!isGltf) return false
    return !item.preview_url
}

function getTypeLabel(ct?: string | null) {
    const t = normalizeContentType(ct)
    if (!t) return 'Unknown'
    if (t.startsWith('image/')) return 'Image'
    if (t.startsWith('video/')) return 'Video'
    if (t.startsWith('audio/')) return 'Audio'
    if (t.includes('text/html')) return 'HTML'
    if (t === 'application/pdf') return 'PDF'
    if (t.startsWith('text/')) return 'Text'
    if (t.includes('model/gltf')) return '3D'
    return 'File'
}

function getTypeIcon(ct?: string | null) {
    const t = normalizeContentType(ct)
    if (t.startsWith('image/')) return ImageIcon
    if (t.startsWith('video/')) return Film
    if (t.startsWith('audio/')) return Music
    if (t.includes('model/gltf')) return Box
    if (t.includes('text/html')) return Globe
    if (t.startsWith('text/') || t === 'application/pdf') return FileText
    return FileText
}

/**
 * Build a CombinedOrdinal from a media-stream item for use in OrdinalDetails / SendOrdinal.
 */
function buildCombinedOrdinalFromStreamItem(
    item: IOrdinalsMediaStreamItem,
    usersOrdinal: UsersOrdinal | null,
): CombinedOrdinal {
    const originParts = String(item.origin || '').split('_')
    return {
        txid: originParts[0] || '',
        vout: Number(originParts[1] || 0),
        outpoint: item.origin || '',
        utxo_outpoint: item.origin || '',
        origin: {
            outpoint: item.origin || '',
            data: {
                insc: {
                    file: {
                        type: item.content_type || 'application/octet-stream',
                    },
                },
                map: {
                    name: item.origin || '',
                },
            },
        },
        treechatData: {
            id: item.id,
            preview_url: item.preview_url,
        } as IOrdinal,
        users_ordinal: usersOrdinal,
        status: item.ownership_status || 'confirmed',
    }
}

type OrdinalsMediaStreamTileProps = {
    item: IOrdinalsMediaStreamItem
    // Rank in the served wall, recorded with the exposure so engagement can
    // later be corrected for position bias.
    position: number
    showOwnerAvatar: boolean
    openRight: (
        filter: Filter,
        opts?: { panelId?: string; meta?: unknown; ensureUnique?: boolean },
    ) => void
    setDetailsItem: (item: IOrdinalsMediaStreamItem) => void
    setFullscreenItem: (item: IOrdinalsMediaStreamItem) => void
    isHtmlType: (ct?: string | null) => boolean
    scrollRoot: Element | null
    ordinalControlsEnabled: boolean
}

function OrdinalsMediaStreamTile({
    item,
    position,
    showOwnerAvatar,
    openRight,
    setDetailsItem,
    setFullscreenItem,
    isHtmlType,
    scrollRoot,
    ordinalControlsEnabled,
}: OrdinalsMediaStreamTileProps) {
    const { ref: inViewRef, inView } = useInView({
        // IMPORTANT: the ordinals stream lives inside a panel scroll container.
        // Observing relative to window (`root: null`) can mark too many tiles as "near viewport",
        // causing large bursts of preview/avatar requests during long scrolls.
        root: scrollRoot,
        // Start capturing a bit before it scrolls fully onscreen.
        rootMargin: '500px 0px 500px 0px',
        threshold: 0,
    })

    const dataUrl = `${window.knovApiUrl}/api/v1/bsv/ordinals/${item.id}/data`
    const t = normalizeContentType(item.content_type)
    const isHtml = isHtmlType(item.content_type)
    const isImage = t.startsWith('image/')

    // TOP NEW seen-state: a dwelled tile counts as "shown" for the ordinal's
    // canonical quest, so scrolling the stream drains the unseen tier. The
    // hook self-gates on the top_new feature and no-ops without a quest id.
    // The ordinal id is passed too so the engagement log still records tiles
    // whose thread has not been created yet — otherwise the ordinals wall would
    // only ever log the inscriptions that already have engagement.
    const { hide: panelHidden, scrollContainerRef, filter } = usePanelContext()
    const tileRef = useRef<HTMLDivElement | null>(null)
    const setTileRef = useCallback(
        (node: HTMLDivElement | null) => {
            tileRef.current = node
            inViewRef(node)
        },
        [inViewRef],
    )
    useQuestImpression({
        elementRef: tileRef,
        questId: item?.parent?.child_quest_id || undefined,
        ordinalId: item?.id,
        spaceId: item?.parent?.space_id,
        position,
        filter,
        rootRef: scrollContainerRef,
        // This wall is not virtualized. Its existing shared near-viewport
        // observer bounds the more detailed qualifying observers.
        disabled: !!panelHidden || !inView,
    })
    const TypeIcon = getTypeIcon(item.content_type)
    const ownerUser = item?.parent?.user || null
    const openOwnerStream = useCallback(
        (ev: React.MouseEvent) => {
            ev.stopPropagation()
            ev.preventDefault()
            if (ownerUser?.id) openRight(withDepth({ user: ownerUser.id }))
        },
        [openRight, ownerUser?.id],
    )
    const openOwnerConversation = useCallback(
        (questId: string) => openRight({ questId }),
        [openRight],
    )
    const ownerHoverCard = useUserHoverCard({
        user: ownerUser,
        disable: !showOwnerAvatar || !ownerUser?.id,
        onOpenStream: openOwnerStream,
        onOpenQuest: openOwnerConversation,
        openOnPress: true,
    })

    const childQuestId = item?.parent?.child_quest_id
    const answerId = item?.parent?.answer_id
    const hasTreechatJump = !!childQuestId && !!answerId
    const recordOrdinalOpen = () => {
        recordEngagementOpen(childQuestId, {
            ordinalId: item.id,
            filter,
            spaceId: item?.parent?.space_id,
        })
    }
    const openOrdinalDetails = (openedItem: IOrdinalsMediaStreamItem = item) => {
        recordOrdinalOpen()
        setDetailsItem(openedItem)
    }
    const openOrdinalFullscreen = () => {
        recordOrdinalOpen()
        setFullscreenItem(item)
    }

    const canAutoSize = t.startsWith('image/') || t.startsWith('video/') || t.startsWith('audio/')

    // The caption redesign makes the media itself the fullscreen target for
    // non-HTML content. Legacy mode keeps the established image maximize button.
    const canClickToFullscreen = ordinalControlsEnabled ? !isHtml : !isHtml && !isImage

    // Loading lots of media tiles over a long scroll can overwhelm the backend (and browsers will
    // eventually stall image loading). Only mount heavy fixed-ratio media near the viewport. HTML
    // uses an always-mounted screenshot shell so intersection changes cannot replace the card or
    // repeatedly create/destroy third-party iframes while the user scrolls. Live HTML only mounts
    // after an explicit play action.
    //
    // NOTE: We intentionally *don't* gate `mediaFrameAuto` (auto-size) because it would
    // collapse to 0 height, preventing `inView` from ever triggering.
    const shouldRenderMedia = isHtml || canAutoSize ? true : inView

    const preferredPreviewUrl = useMemo(() => {
        const urls = item.preview_urls
        const fromMap = urls && urls.list ? urls.list : null
        const original = urls?.original || null
        // Prefer CDN links when possible. If the sized variant isn't cached yet, fall back to the
        // original blob CDN URL (still avoids hitting Rails), then finally the legacy preview endpoint.
        return fromMap || original || item.preview_url || undefined
    }, [item])

    // Open fullscreen only from non-interactive media surfaces. Embedded media
    // controls stop propagation so playback controls keep their native behavior.
    const handleOrdinalClick = (ev: React.MouseEvent) => {
        if (!canClickToFullscreen) return
        // Don't trigger if clicking on a button or interactive element
        const target = ev.target as HTMLElement
        if (
            target.closest('button') ||
            target.closest('video') ||
            target.closest('audio') ||
            target.closest('[data-no-modal-interaction]')
        ) {
            return
        }
        ev.stopPropagation()
        ev.preventDefault()
        openOrdinalFullscreen()
    }

    return (
        <div
            className={styles.tile}
            ref={setTileRef}
            data-testid="ordinal-stream-tile"
            data-ordinal-id={item.id}
        >
            <div
                // Stream menus use a larger, edge-anchored target than wrapper
                // artifact menus. Keep a distinct marker so nested audio-card
                // controls can clear it without inheriting wrapper rail offsets.
                data-ordinal-stream-media-frame
                className={cn(
                    styles.mediaFrame,
                    canAutoSize ? styles.mediaFrameAuto : styles.mediaFrameFixed,
                    isHtml && styles.mediaFrameHtml,
                    canClickToFullscreen && styles.mediaFrameClickable,
                )}
                onClick={canClickToFullscreen ? handleOrdinalClick : undefined}
            >
                {shouldRenderMedia ? (
                    <OrdinalsRender
                        outpoint={item.origin || ''}
                        url={dataUrl}
                        bsvOrdinalId={item.id}
                        // HTML stream cards are not subscribed to AnswerChannel updates. Omitting
                        // the answer id keeps HTMLOrdinalLoader's persisted URL-preview lookup on.
                        answerId={isHtml ? undefined : item.parent?.answer_id || undefined}
                        artifactOrdinalId={item.id}
                        artifactOwnerId={item.owner_id || undefined}
                        showBidAsk={true}
                        marketSummary={item.market}
                        contentType={item.content_type || ''}
                        previewUrl={preferredPreviewUrl}
                        previewSize="list"
                        autoCapture3dPreview={shouldAutoCapture3dPreview(item, inView)}
                        // This is a feed/grid preview: prefer preview variants for images and avoid
                        // interactive behaviors inside the tile.
                        allowInteraction={false}
                        htmlAlign="center"
                        passiveHtmlPreview={isHtml}
                        htmlPreviewLookupEnabled={!isHtml || inView}
                        title={getImageAlt(item)}
                        filename={item.filename || undefined}
                        className={styles.ordinalRender}
                        naturalVideo={t.startsWith('video/')}
                        audioPalette="orange"
                    />
                ) : (
                    <div
                        className={styles.mediaFallback}
                        aria-label={getTypeLabel(item.content_type)}
                    >
                        <TypeIcon size={26} className={styles.fallbackIcon} />
                        <div className={styles.fallbackLabel}>
                            {getTypeLabel(item.content_type)}
                        </div>
                    </div>
                )}

                {(item.duplicate_count || 1) > 1 &&
                    // Audio/video rows auto-size to roughly the native control bar,
                    // so an overlaid badge would cover the playback controls.
                    !(t.startsWith('audio/') || t.startsWith('video/')) && (
                        <div className={styles.duplicateCountBadge}>×{item.duplicate_count}</div>
                    )}

                {item.ownership_status === 'unconfirmed' ? (
                    <div className={styles.unconfirmedBadge} data-testid="ordinal-ownership-status">
                        Pending
                    </div>
                ) : (
                    item.lens_stale && (
                        // A live event said this row no longer matches the lens
                        // that fetched it. Marked, not removed: ranked walls only
                        // change composition on an explicit refresh, and pulling
                        // a card out from under a reader is the behavior this
                        // whole change exists to stop.
                        <div className={styles.lensStaleBadge} data-testid="ordinal-lens-status">
                            {item.lens_stale_reason === 'unlisted' ? 'Unlisted' : 'Sold'}
                        </div>
                    )
                )}

                {shouldRenderMedia && showOwnerAvatar && ownerUser?.id && (
                    <button
                        type="button"
                        className={styles.ownerJump}
                        data-testid="ordinal-owner-avatar"
                        data-owner-id={ownerUser.id}
                        aria-label="Open user"
                        title={
                            ownerHoverCard.enabled
                                ? undefined
                                : ownerUser?.name || ownerUser?.handle || ownerUser?.username || ''
                        }
                        onClick={ev => {
                            if (ownerHoverCard.openFromPress(ev)) return
                            openOwnerStream(ev)
                        }}
                        onMouseDown={ev => ev.stopPropagation()}
                        onPointerDown={ev => ev.stopPropagation()}
                        {...ownerHoverCard.triggerProps}
                    >
                        <UserIcon
                            user={ownerUser}
                            size="small"
                            contextStyles={styles.ownerUserIcon}
                        />
                    </button>
                )}
                {shouldRenderMedia && showOwnerAvatar && !ownerUser?.id && (
                    <span
                        className={cn(styles.ownerJump, styles.ownerFallback)}
                        aria-label="No Treechat owner linked"
                        title="No Treechat owner linked"
                    >
                        <TreechatIcon size={24} className={styles.ownerUserIcon} />
                    </span>
                )}
                {ownerHoverCard.portal}

                {!ordinalControlsEnabled && isImage && (
                    <button
                        type="button"
                        className={styles.maximizeHint}
                        onClick={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            openOrdinalFullscreen()
                        }}
                        onMouseDown={ev => ev.stopPropagation()}
                        onPointerDown={ev => ev.stopPropagation()}
                        aria-label="Fullscreen"
                        title="Fullscreen"
                    >
                        <Maximize2 size={18} strokeWidth={2.2} />
                    </button>
                )}

                {!ordinalControlsEnabled && (
                    <button
                        type="button"
                        className={cn(
                            styles.ordinalDetailsJump,
                            !hasTreechatJump && styles.ordinalDetailsJumpSolo,
                        )}
                        aria-label="Open ordinal details"
                        title="Open ordinal details"
                        onClick={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            openOrdinalDetails()
                        }}
                        onMouseDown={ev => ev.stopPropagation()}
                        onPointerDown={ev => ev.stopPropagation()}
                    >
                        <Circle size={18} className={styles.ordinalsIcon} />
                    </button>
                )}

                {!ordinalControlsEnabled && hasTreechatJump && (
                    <button
                        type="button"
                        className={styles.treechatJump}
                        aria-label="Open in Treechat"
                        title="Open in Treechat"
                        onClick={ev => {
                            ev.stopPropagation()
                            ev.preventDefault()
                            recordOrdinalOpen()
                            openRight({ questId: childQuestId, answerId })
                        }}
                        onMouseDown={ev => ev.stopPropagation()}
                        onPointerDown={ev => ev.stopPropagation()}
                    >
                        <TreechatIcon
                            // @ts-expect-error - TreechatIcon lacks contextStyles in its type definition
                            contextStyles={styles.treechatIcon}
                            className={styles.treechatIcon}
                        />
                    </button>
                )}
            </div>

            {ordinalControlsEnabled && (
                <OrdinalActionDock
                    item={item}
                    variant="caption"
                    openRight={openRight}
                    onDetails={openOrdinalDetails}
                />
            )}
        </div>
    )
}

export default function OrdinalsMediaStream({ filter }: OrdinalsMediaStreamProps) {
    const { panel, scrollContainerRef, setIsRefreshing } = usePanelContext()
    const panelId = panel?.panelId
    const { openRight } = useOpenRight(panelId)
    const [detailsItem, setDetailsItem] = useState<IOrdinalsMediaStreamItem | null>(null)
    const [fullscreenItem, setFullscreenItem] = useState<IOrdinalsMediaStreamItem | null>(null)
    const [sendTrayOpen, setSendTrayOpen] = useState(false)
    const [scrollRoot, setScrollRoot] = useState<Element | null>(null)
    const isUserOrdinals =
        !!filter?.user_ordinals || (!!filter?.ordinals && filter?.ordinals_scope === 'mine')
    const isMineOrdinals = !!filter?.ordinals && filter?.ordinals_scope === 'mine'
    const showOwnerAvatar = shouldShowOrdinalOwnerAvatar(filter)
    const isGlobalOrdinals = !isUserOrdinals && !!filter?.ordinals
    const resolvedMinSats = isGlobalOrdinals ? resolveOrdinalsMinSats(filter) : 0
    // The empty state quotes the dial, so it reads in the viewer's currency too.
    const useUsd = useStore(state => state.bsvUnit === USD_UNIT)
    const exchangeRate = useStore(state => state.exchangeRate)
    const { setStreamFilter } = useStreamFilter(panelId, { append: false })
    const ordinalControlsEnabled = getOrdinalControlsMode() === 'caption'
    // The Market tab is the global stream filtered to ordinals with an open ask.
    const marketFilterActive = isGlobalOrdinals && filter?.market === true

    // Fullscreen UX: lock scrolling + allow ESC to exit.
    useEffect(() => {
        if (!fullscreenItem) return

        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFullscreenItem(null)
        }
        window.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = prevOverflow
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [fullscreenItem])

    // Capture the panel's scroll container element so children can use it as IntersectionObserver root.
    // (Ref `.current` changes don't trigger rerenders, so we store it in state.)
    useEffect(() => {
        let raf = window.requestAnimationFrame(() => {
            const el = scrollContainerRef.current
            if (el && el !== scrollRoot) setScrollRoot(el)
        })
        return () => window.cancelAnimationFrame(raf)
    }, [scrollContainerRef, scrollRoot])

    // The effective filter carries the resolved quality-dial stop for the
    // public wall so the query key matches the bar applied server-side.
    const effectiveFilter = useMemo(() => ordinalsStreamQueryFilter(withDepth(filter)), [filter])
    const filterKey = useMemo(() => serializeFilterKey(effectiveFilter), [effectiveFilter])

    const query = useInfiniteQuery({
        queryKey: ['ordinalsMediaStream', filterKey],
        queryFn: ({ pageParam }) =>
            api.ordinalsMediaStream({
                filter: {
                    ordinals: !!filter?.ordinals && !filter?.user_ordinals,
                    user_ordinals: filter?.user_ordinals,
                    ordinals_scope: filter?.ordinals_scope,
                    // Stream order from the shared StreamOrder pills.
                    order: typeof filter?.order === 'string' ? filter.order : undefined,
                    // Top 24h / Week / … cut by creation time on the wall and by
                    // listing time on the Market tab; only plain Top takes one.
                    top_window: ordinalsTopWindowParam(filter?.order, filter?.top_window),
                    market: marketFilterActive,
                    // The Market lens shares the wall's quality dial.
                    min_sats: isGlobalOrdinals ? effectiveFilter.min_sats : undefined,
                },
                page: pageParam || 1,
                per_page: 60,
            }),
        structuralSharing: preserveOrdinalStreamState,
        initialPageParam: 1,
        getNextPageParam: (lastPage, pages) => resolveNextOrdinalMediaPage(lastPage, pages.length),
        // Ranked rows stay frozen until the shared top pill, navigation, or PTR
        // explicitly invalidates this query — including My Ordinals, which used
        // to poll every 10s while a purchase was pending. Ownership now flips
        // from an ordinal state event stamped server-side
        // (docs/ordinal-state-events.md §4), so nothing here polls at all.
        refetchOnMount: refetchStreamAfterExplicitInvalidation,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        retry: isMineOrdinals ? shouldRetryWalletSnapshotQuery : 3,
    })

    const items = useMemo(() => {
        const flat = (query.data?.pages || []).flatMap(p => p.items || [])
        // The global stream is deduped + rank-ordered server side, but offset pagination
        // over a stream whose ranks shift between fetches can repeat an item on a later
        // page — and a duplicate group's representative can even come back as a different
        // ordinal id. Keep only the first occurrence of each id / content identity.
        const seenIds = new Set<string>()
        const seenContentKeys = new Set<string>()
        return flat.filter(item => {
            if (!item?.id || seenIds.has(item.id)) return false
            seenIds.add(item.id)
            if (item.content_key) {
                if (seenContentKeys.has(item.content_key)) return false
                seenContentKeys.add(item.content_key)
            }
            return true
        })
    }, [query.data?.pages])

    const onScroll = useCallback(() => {
        const el = scrollContainerRef.current
        if (!el) return
        if (query.isFetchingNextPage || !query.hasNextPage) return
        const { scrollTop, scrollHeight, clientHeight } = el
        if (scrollTop + clientHeight >= scrollHeight - 800) {
            query.fetchNextPage()
        }
    }, [query])

    useEffect(() => {
        const el = scrollContainerRef.current
        if (!el) return
        el.addEventListener('scroll', onScroll)
        return () => el.removeEventListener('scroll', onScroll)
    }, [onScroll])

    // Integrate with PanelContainer pull-to-refresh indicator.
    // PanelContainer toggles isRefreshing when the gesture triggers; we keep it in-sync with query fetching.
    useEffect(() => {
        if (!setIsRefreshing) return
        if (query.isFetching && !query.isFetchingNextPage && items.length > 0) {
            setIsRefreshing(true)
        } else {
            setIsRefreshing(false)
        }
    }, [setIsRefreshing, query.isFetching, query.isFetchingNextPage, items.length])

    const currentUserId = gon.currentUser?.id
    const detailsOwnerId = detailsItem?.parent?.user?.id
    const showOwnerActions =
        !!currentUserId && !!detailsOwnerId
            ? String(detailsOwnerId) === String(currentUserId)
            : false

    // `OrdinalDetails` expects `users_ordinal` to compute "active" state for Hide / Show-in-profile.
    // The media stream payload doesn't include it, so we lazily load the current user's ordinals
    // (cached by React Query) when viewing one of our own ordinals.
    //
    // IMPORTANT: Hooks must be called unconditionally (before early returns).
    const { data: treechatOrdinals } = useQuery({
        queryKey: ['treechatOrdinals', currentUserId],
        queryFn: async () => {
            const result = await api.listOrdinals()
            return result?.ordinals
        },
        enabled: !!currentUserId && showOwnerActions,
    })

    const usersOrdinalForDetails = useMemo(() => {
        if (!showOwnerActions) return null
        if (!detailsItem?.id) return null
        if (!Array.isArray(treechatOrdinals)) return null
        const match = treechatOrdinals.find(
            (o: IOrdinal) => String(o?.id || '') === String(detailsItem.id),
        )
        return match?.users_ordinal || null
    }, [showOwnerActions, detailsItem?.id, treechatOrdinals])

    const walletSnapshot = query.data?.pages?.[0]?.wallet_snapshot
    const staleWalletWarning = walletSnapshot?.status === 'stale' && (
        <div className={styles.snapshotWarning} role="status">
            Showing the last successful wallet update because live wallet data is temporarily
            unavailable.
        </div>
    )
    const partialWalletWarning = walletSnapshot?.status === 'partial' && (
        <div className={styles.snapshotWarning} role="status">
            This wallet update is incomplete. Some ordinals may be missing while live data is being
            refreshed.
        </div>
    )
    const resolvingWalletCount =
        walletSnapshot?.status === 'fresh'
            ? Math.max(walletSnapshot.ordinal_count - walletSnapshot.indexed_count, 0)
            : 0
    const resolvingWalletNotice = resolvingWalletCount > 0 && (
        <div className={styles.snapshotWarning} role="status">
            Resolving {resolvingWalletCount} {resolvingWalletCount === 1 ? 'ordinal' : 'ordinals'}{' '}
            from this wallet…
        </div>
    )

    if (query.isLoading) {
        return (
            <div className={styles.loading}>
                <i className="fa fa-circle-o-notch fa-spin fa-lg" />
            </div>
        )
    }

    if (query.isError) {
        return (
            <div className={styles.error}>
                {isMineOrdinals
                    ? 'We couldn’t refresh this wallet’s ordinals. Try again.'
                    : 'Failed to load ordinals.'}
            </div>
        )
    }

    if (!items.length) {
        if (isGlobalOrdinals && resolvedMinSats > 0) {
            return (
                <>
                    <div className={styles.empty}>
                        <div>
                            No {marketFilterActive ? 'listings' : 'ordinals'} above{' '}
                            {formatOrdinalsMinSatsAmount(resolvedMinSats, useUsd, {
                                compact: true,
                                exchangeRate,
                            })}{' '}
                            yet.
                        </div>
                        <button
                            type="button"
                            className={styles.emptyAction}
                            onClick={() =>
                                applyOrdinalsMinSats({
                                    filter,
                                    setStreamFilter,
                                    value: 0,
                                    fromLocation: 'ordinals_empty_state',
                                })
                            }
                        >
                            Show all
                        </button>
                    </div>
                </>
            )
        }
        if (marketFilterActive) {
            return <div className={styles.empty}>Nothing listed for sale yet.</div>
        }
        return (
            <>
                {staleWalletWarning}
                {partialWalletWarning}
                {resolvingWalletNotice}
                {resolvingWalletCount === 0 && <div className={styles.empty}>No ordinals yet.</div>}
            </>
        )
    }

    const closeDetails = () => {
        setSendTrayOpen(false)
        setDetailsItem(null)
    }
    const closeFullscreen = () => setFullscreenItem(null)

    // HTML previews remain inline and manage their live interaction separately.
    const isHtmlType = (ct?: string | null) => normalizeContentType(ct).includes('text/html')

    const openDetails = (item: IOrdinalsMediaStreamItem) => {
        setSendTrayOpen(false)
        setDetailsItem(item)
    }

    return (
        <>
            {staleWalletWarning}
            {partialWalletWarning}
            {resolvingWalletNotice}
            <div className={cn(styles.scroller, isMobile && 'full-bleed-quests')}>
                <div className={cn(styles.grid, styles.list)}>
                    {items.map((item, ix) => (
                        // The wall renders arbitrary on-chain content — one bad
                        // inscription must never take down the whole stream.
                        <ErrorBoundary key={item.id} label={`OrdinalsMediaStreamTile ${item.id}`}>
                            <OrdinalsMediaStreamTile
                                item={item}
                                position={ix}
                                showOwnerAvatar={showOwnerAvatar}
                                openRight={openRight}
                                setDetailsItem={openDetails}
                                setFullscreenItem={(i: IOrdinalsMediaStreamItem) =>
                                    setFullscreenItem(i)
                                }
                                isHtmlType={isHtmlType}
                                scrollRoot={scrollRoot}
                                ordinalControlsEnabled={ordinalControlsEnabled}
                            />
                        </ErrorBoundary>
                    ))}
                </div>

                {query.isFetchingNextPage && (
                    <div className={cn(styles.loadingMore)}>
                        <i className="fa fa-circle-o-notch fa-spin fa-lg" />
                    </div>
                )}
            </div>

            {fullscreenItem &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={styles.fullscreenOverlay}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Fullscreen ordinal"
                        onClick={e => {
                            if (e.target === e.currentTarget) closeFullscreen()
                        }}
                    >
                        <div
                            className={styles.fullscreenInner}
                            onClick={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            onPointerDown={e => e.stopPropagation()}
                        >
                            <ErrorBoundary
                                label={`OrdinalsMediaStream fullscreen ${fullscreenItem.id}`}
                            >
                                <OrdinalsRender
                                    outpoint={fullscreenItem.origin || ''}
                                    url={`${window.knovApiUrl}/api/v1/bsv/ordinals/${fullscreenItem.id}/data`}
                                    bsvOrdinalId={fullscreenItem.id}
                                    answerId={
                                        isHtmlType(fullscreenItem.content_type)
                                            ? undefined
                                            : fullscreenItem.parent?.answer_id || undefined
                                    }
                                    artifactOrdinalId={fullscreenItem.id}
                                    artifactOwnerId={fullscreenItem.owner_id || undefined}
                                    contentType={fullscreenItem.content_type || ''}
                                    previewUrl={
                                        fullscreenItem.preview_urls?.original ||
                                        fullscreenItem.preview_url ||
                                        undefined
                                    }
                                    allowInteraction={true}
                                    fillVideoContainer
                                    title={getImageAlt(fullscreenItem)}
                                    filename={fullscreenItem.filename || undefined}
                                    className={styles.fullscreenEmbed}
                                    audioPalette="orange"
                                />
                            </ErrorBoundary>
                            <button
                                type="button"
                                className={styles.fullscreenCloseBtn}
                                onClick={closeFullscreen}
                                aria-label="Close fullscreen"
                                title="Close fullscreen"
                            >
                                <X size={20} strokeWidth={2.2} />
                            </button>

                            {ordinalControlsEnabled && (
                                <OrdinalActionDock
                                    item={fullscreenItem}
                                    variant="fullscreen"
                                    openRight={openRight}
                                    onBeforeNavigate={closeFullscreen}
                                    onDetails={item => {
                                        closeFullscreen()
                                        openDetails(item)
                                    }}
                                />
                            )}
                        </div>
                    </div>,
                    document.body,
                )}

            {detailsItem &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className={viewerStyles.lightbox}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Ordinal details"
                        onClick={e => {
                            if (e.target === e.currentTarget) closeDetails()
                        }}
                    >
                        <OrdinalDetails
                            selectedOrdinal={{
                                ordinal: buildCombinedOrdinalFromStreamItem(
                                    detailsItem,
                                    usersOrdinalForDetails,
                                ),
                                content: null,
                                isLoading: false,
                                error: null,
                                forceLive: false,
                            }}
                            inscriptionOutpoint={detailsItem.origin || ''}
                            onClose={closeDetails}
                            onSendClick={() => setSendTrayOpen(true)}
                            showShowInProfile={showOwnerActions}
                            showHidden={showOwnerActions}
                            showSend={showOwnerActions}
                        />

                        {showOwnerActions && sendTrayOpen && (
                            <SendOrdinal
                                ordinal={buildCombinedOrdinalFromStreamItem(
                                    detailsItem,
                                    usersOrdinalForDetails,
                                )}
                                onDismiss={() => setSendTrayOpen(false)}
                                onRefreshOrdinals={() => {
                                    query.refetch()
                                }}
                            />
                        )}
                    </div>,
                    document.body,
                )}
        </>
    )
}
